import React, { useState, useMemo, useRef, useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { useRouter } from 'next/router'
import cn from 'classnames'

import type { Availability } from 'src/schemas/availability'

import Room from 'client/components/Rooms/RoomResponsive'

import { getRoomsByName } from './utils'
import { SHOW_MORE_LIMIT } from './constants'
import styles from './styles.module.css'

import ShowMore from 'client/components/Rooms/RoomResponsive/ShowMore'
import { useDevice } from 'src/hooks/useDevice'
import { BreakpointSizes } from 'src/types/device'
import { SOURCE_META } from 'src/constants/sources'
import useExperiment from 'src/hooks/useExperimentConfig'
import { useEffectOnce } from 'react-use'
import { HIGHLIGHTED_OFFER_EXPERIMENT } from 'src/constants/experimentConfig'

export type Board = Availability['boards'][0]

interface IRoomsCollection {
  closeGroup: () => void
  isOpen: boolean
  rooms: Board[]
  withHeader?: boolean
}

const RoomsCollection = ({
  isOpen,
  closeGroup,
  rooms = [],
  withHeader = false,
}: IRoomsCollection) => {
  const router = useRouter()
  const highlightedRoomRef = useRef<HTMLLIElement>(null)

  const { variant, notify } = useExperiment(HIGHLIGHTED_OFFER_EXPERIMENT.ID)
  useEffectOnce(notify)

  const roomsSorted = useMemo(() => getRoomsByName(rooms), [rooms])

  const { total, price, source, aid } = router.query

  const priceQuery = parseInt(total as string, 10) || parseInt(price as string, 10)

  const affiliateBoard =
    roomsSorted.flat().find(board => priceQuery === board.total)?.availabilityId ?? ''

  const isFromMeta = source === SOURCE_META
  const affiliateId = aid as string | undefined

  // Find the index of the room containing the affiliate board
  const highlightedRoomIndex = useMemo(() => {
    if (!affiliateBoard || !isFromMeta) return -1
    return roomsSorted.findIndex(room =>
      room.some(board => board.availabilityId === affiliateBoard)
    )
  }, [roomsSorted, affiliateBoard, isFromMeta])

  // For Variant 1: Reorder rooms to promote highlighted room to first position
  const roomsOrdered = useMemo(() => {
    if (variant === HIGHLIGHTED_OFFER_EXPERIMENT.PROMOTE_TO_FIRST && highlightedRoomIndex > 0) {
      const reordered = [...roomsSorted]
      const [highlightedRoom] = reordered.splice(highlightedRoomIndex, 1)
      reordered.unshift(highlightedRoom)
      return reordered
    }
    return roomsSorted
  }, [roomsSorted, highlightedRoomIndex, variant])

  const totalRooms = roomsOrdered.length

  const showMoreLimit = SHOW_MORE_LIMIT

  const [visibleRoomsCount, setVisibleRoomsCount] = useState(showMoreLimit)

  const incrementVisibleRooms = () => {
    setVisibleRoomsCount(prevCount => prevCount + showMoreLimit)
  }

  const roomsToDisplay = useMemo(
    () => roomsOrdered.slice(0, visibleRoomsCount),
    [roomsOrdered, visibleRoomsCount]
  )

  // For Variant 2: Scroll to highlighted room position
  useEffect(() => {
    if (
      variant === HIGHLIGHTED_OFFER_EXPERIMENT.SCROLL_TO_POSITION &&
      highlightedRoomRef.current &&
      isFromMeta &&
      affiliateBoard
    ) {
      // Small delay to ensure the DOM is ready
      const timeoutId = setTimeout(() => {
        highlightedRoomRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
      }, 100)
      return () => clearTimeout(timeoutId)
    }
    return undefined
  }, [variant, isFromMeta, affiliateBoard])

  const moreRooms = totalRooms - visibleRoomsCount

  const { activeBreakpoint } = useDevice()
  const isMobile = activeBreakpoint < BreakpointSizes.M

  const getShowMoreComponent = () => {
    return (
      <div className={styles.showMoreRoomsContainer}>
        <ShowMore onClick={incrementVisibleRooms} outlinedShowMoreButton={isMobile}>
          <FormattedMessage id="global.show-more-rooms-from" values={{ rooms: moreRooms }} />
        </ShowMore>
      </div>
    )
  }

  // Check if a room is the highlighted room
  const isHighlightedRoom = (room: Board[]) =>
    Boolean(affiliateBoard && room.some(board => board.availabilityId === affiliateBoard))

  return (
    <div
      className={cn(
        styles.roomCollection,
        { [styles.hidden]: !isOpen },
        { [styles.withHeader]: isOpen && withHeader }
      )}
    >
      <ul data-testid="rooms-list">
        {roomsToDisplay.map((room, index) => {
          const isHighlighted = isHighlightedRoom(room)
          // For variants 1 and 2, highlighted room should always be open
          const shouldBeOpen =
            index === 0 ||
            ((variant === HIGHLIGHTED_OFFER_EXPERIMENT.PROMOTE_TO_FIRST ||
              variant === HIGHLIGHTED_OFFER_EXPERIMENT.SCROLL_TO_POSITION) &&
              isHighlighted)

          return (
            <li
              key={`${room[0]?.roomUniqueId}-${room[0].roomIndex}`}
              className={styles.item}
              data-testid="rooms-list-item"
              data-mapped={!!room[0].normalizedRoomCode}
              ref={isHighlighted ? highlightedRoomRef : undefined}
            >
              <Room
                room={room}
                closeGroup={closeGroup}
                affiliateBoard={affiliateBoard}
                isFromMeta={isFromMeta}
                initialOpenOffers={shouldBeOpen}
                experimentVariant={variant}
                affiliateId={affiliateId}
              />
            </li>
          )
        })}
      </ul>

      {moreRooms > 0 && getShowMoreComponent()}
    </div>
  )
}

export default RoomsCollection
